/* ============================================================
   CPFS product (PDP) behaviours — three dependency-free custom
   elements. No framework, no GSAP (Decision 2 / D2-3).

   1. <cpfs-quote-drawer>  slide-in quote form: open on any
      [data-quote-open], close on backdrop / [data-quote-close] /
      Escape, focus-trapped, body-scroll locked. Toggles the
      non-HORECA business-type gate.

   2. <cpfs-orderable-cta>  ecom-vs-quote branch (Decision 3).
      The server renders the correct CTA for the first variant;
      this element rides Dawn's PUB_SUB_EVENTS.variantChange (see
      product-info.js) to flip Add-to-cart <-> Request a quotation
      for the 9 mixed products when the shopper switches variant,
      and keeps the drawer's SKU selection in sync.
   ============================================================ */
(function () {
  'use strict';

  var REDIRECT_TYPES = ['Supermarket/ Wholesaler', 'Personal Use'];

  function setText(selector, text) {
    document.querySelectorAll(selector).forEach(function (el) {
      el.textContent = text;
      // A fact row with no value is hidden rather than shown as "Pending", so a
      // variant switch has to restore the row when the new variant does have a
      // value (and hide it again when it does not). Server-side render sets the
      // initial state; this keeps it honest across variant changes.
      var row = el.closest('.v3-fact');
      if (row) row.hidden = !text;
    });
  }

  // 3. <cpfs-gallery> — the main pack-shot image follows thumbnail clicks AND
  //    variant switches. Thumbs are <button>s, so Enter/Space and focus come
  //    for free. Dawn's product-info.js updateMedia() only knows how to drive
  //    its own <media-gallery> (which this custom gallery replaced), so the
  //    variant path rides PUB_SUB_EVENTS.variantChange — same pattern as
  //    <cpfs-orderable-cta> — and lifts the re-rendered main image out of the
  //    section HTML Dawn already fetched. No extra network request.
  if (!customElements.get('cpfs-gallery')) {
    customElements.define(
      'cpfs-gallery',
      class CpfsGallery extends HTMLElement {
        connectedCallback() {
          this.mainImg = this.querySelector('.v3-gallery__main img');
          this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));
          if (!this.mainImg) return;

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
          }

          if (this.thumbs.length < 2) return;
          this._onClick = this.onClick.bind(this);
          this.addEventListener('click', this._onClick);
        }
        disconnectedCallback() {
          if (this._onClick) this.removeEventListener('click', this._onClick);
          if (this._unsubscribe) this._unsubscribe();
        }
        setMain(src, srcset, alt) {
          if (!src) return;
          if (srcset) this.mainImg.srcset = srcset;
          this.mainImg.src = src;
          if (alt) this.mainImg.alt = alt;
        }
        setActiveThumb(activeBtn) {
          this.thumbs.forEach(function (t) {
            var active = t === activeBtn;
            t.classList.toggle('is-active', active);
            if (active) t.setAttribute('aria-current', 'true');
            else t.removeAttribute('aria-current');
          });
        }
        onClick(e) {
          var btn = e.target.closest('[data-gallery-thumb]');
          if (!btn || !this.contains(btn)) return;
          this.setMain(btn.getAttribute('data-full'), btn.getAttribute('data-srcset'), btn.getAttribute('data-alt'));
          this.setActiveThumb(btn);
        }
        onVariantChange(event) {
          var data = event && event.data;
          if (!data || !data.variant || !data.html) return;
          var section = this.dataset.section;
          if (section && data.sectionId && data.sectionId.toString() !== section.toString()) return;
          // A variant without its own image keeps the current photo (Dawn behaviour).
          var media = data.variant.featured_media;
          if (!media || !media.id) return;
          var fresh = data.html.querySelector('cpfs-gallery .v3-gallery__main img');
          if (!fresh) return;
          this.setMain(fresh.getAttribute('src'), fresh.getAttribute('srcset'), fresh.getAttribute('alt'));
          var match = null;
          this.thumbs.forEach(function (t) {
            if (t.getAttribute('data-media-id') === String(media.id)) match = t;
          });
          this.setActiveThumb(match);
        }
      },
    );
  }

  if (!customElements.get('cpfs-quote-drawer')) {
    customElements.define(
      'cpfs-quote-drawer',
      class CpfsQuoteDrawer extends HTMLElement {
        connectedCallback() {
          this.drawer = this.querySelector('[data-quote-drawer]');
          this.backdrop = this.querySelector('[data-quote-backdrop]');
          this.opener = null;

          this.onDocClick = this.onDocClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.close = this.close.bind(this);

          document.addEventListener('click', this.onDocClick);
          document.addEventListener('keydown', this.onKeydown);

          this.querySelectorAll('[data-quote-close]').forEach(function (btn) {
            btn.addEventListener('click', this.close);
          }, this);
          if (this.backdrop) this.backdrop.addEventListener('click', this.close);

          var typeSelect = this.querySelector('[data-business-type]');
          if (typeSelect) typeSelect.addEventListener('change', this.syncGate.bind(this));

          // Re-open after a contact-form post (success OR error) so the message shows.
          var status = this.drawer && this.drawer.querySelector('.v3-form__status');
          if (status) {
            this.open();
            if (typeof status.focus === 'function') status.focus();
          }
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onDocClick);
          document.removeEventListener('keydown', this.onKeydown);
          // Release the scroll lock if the element is torn down while open (editor re-render).
          document.body.style.overflow = '';
        }

        onDocClick(event) {
          var trigger = event.target.closest && event.target.closest('[data-quote-open]');
          if (!trigger) return;
          event.preventDefault();
          this.opener = trigger;
          this.open();
        }

        onKeydown(event) {
          if (!this.isOpen()) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
          } else if (event.key === 'Tab') {
            this.trapFocus(event);
          }
        }

        isOpen() {
          return this.drawer && this.drawer.classList.contains('is-open');
        }

        open() {
          if (!this.drawer) return;
          this.drawer.classList.add('is-open');
          this.drawer.setAttribute('aria-hidden', 'false');
          if (this.backdrop) this.backdrop.classList.add('is-open');
          document.body.style.overflow = 'hidden';
          var focusable = this.focusable();
          if (focusable.length) focusable[0].focus();
        }

        close() {
          if (!this.drawer) return;
          this.drawer.classList.remove('is-open');
          this.drawer.setAttribute('aria-hidden', 'true');
          if (this.backdrop) this.backdrop.classList.remove('is-open');
          document.body.style.overflow = '';
          if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
        }

        focusable() {
          if (!this.drawer) return [];
          return Array.prototype.slice
            .call(
              this.drawer.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
              ),
            )
            .filter(function (el) {
              return el.offsetParent !== null;
            });
        }

        trapFocus(event) {
          var items = this.focusable();
          if (!items.length) return;
          var first = items[0];
          var last = items[items.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        syncGate(event) {
          var gate = this.querySelector('[data-quote-gate]');
          var rest = this.querySelector('[data-quote-rest]');
          var redirect = REDIRECT_TYPES.indexOf(event.target.value) !== -1;
          if (gate) gate.hidden = !redirect;
          if (rest) rest.hidden = redirect;
        }
      },
    );
  }

  if (!customElements.get('cpfs-orderable-cta')) {
    customElements.define(
      'cpfs-orderable-cta',
      class CpfsOrderableCta extends HTMLElement {
        connectedCallback() {
          this.section = this.dataset.section;
          this.map = this.parseMap();
          this.unsubscribe = null;

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
          }
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
        }

        parseMap() {
          var script = this.querySelector('[data-variant-map]');
          if (!script) return {};
          try {
            return JSON.parse(script.textContent) || {};
          } catch (e) {
            return {};
          }
        }

        onVariantChange(event) {
          var data = event && event.data;
          if (!data || !data.variant) return;
          if (this.section && data.sectionId && data.sectionId.toString() !== this.section.toString()) return;
          this.apply(data.variant.id);
        }

        apply(variantId) {
          var v = this.map[variantId] || {};
          var orderable = v.orderable === true;

          // Ecom vs quote CTA + ecom-only facts.
          this.toggle('[data-cpfs-cta="cart"]', !orderable);
          this.toggle('[data-cpfs-cta="quote"]', orderable);
          document.querySelectorAll('[data-cpfs-ecom]').forEach(function (el) {
            el.hidden = !orderable;
          });

          // Per-variant bound text (SKU line + facts + pill + shelf life) — Dawn only
          // partial-swaps its own ids, so we re-sync these ourselves (H3/H4).
          if (v.sku) setText('[data-cpfs-sku]', v.sku);
          if (v.case) setText('[data-cpfs-case]', v.case);
          if (v.shelf) setText('[data-cpfs-shelf]', v.shelf);
          if (v.price) setText('[data-cpfs-price]', v.price);

          // Keep the drawer's SKU selection in sync with the chosen variant.
          var skuSelect = document.querySelector('[data-quote-sku]');
          if (skuSelect) {
            Array.prototype.forEach.call(skuSelect.options, function (opt) {
              if (opt.dataset.variantId && opt.dataset.variantId.toString() === variantId.toString()) {
                skuSelect.value = opt.value;
              }
            });
          }
        }

        toggle(selector, hidden) {
          var el = this.querySelector(selector);
          if (el) el.hidden = hidden;
        }
      },
    );
  }
})();
